import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';

import { budgetCategoryGraph } from 'lib/api/budget';
import { setNumberFormat } from 'lib/commonLib';

import CurrentStatus from 'components/atoms/CurrentStatus';
import ContentsWrap from 'components/molecules/ContentsWrap';
import Highchart from 'components/organisms/Chart/Highchart';
import SwitchingArea from 'components/organisms/SwitchingArea';

const StatisticsGraph = (props) => {
  const { handleClickSnackbar, params } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const initOptions = {
    category: {
      chart: { type: 'line', backgroundColor: theme.palette.common.bgColor },
      colors: ['#000'],
      title: {
        text: t('카테고리 별 예산 비중도'),
        style: { fontWeight: 'bold' },
      },
      credits: { enabled: false },
      legend: { enabled: true },
      xAxis: {
        type: 'category',
        plotLines: [
          {
            label: { align: 'center', style: { color: 'gray' } },
            zIndex: 10000,
          },
        ],
      },
      series: [],
    },
    product: {
      chart: { type: 'bar', backgroundColor: theme.palette.common.bgColor },
      title: {
        text: t('상품별 예산 비중도'),
        style: { fontWeight: 'bold' },
      },
      credits: { enabled: false }, // remove highChart Logo
      xAxis: { categories: [] },
      yAxis: [
        {
          min: 0,
          title: {
            align: 'high',
            textAlign: 'left',
            rotation: 0,
            offset: 0,
            margin: 0,
            y: -10,
            x: -65,
          },
          plotLines: [],
          labels: { format: '{value:.,0f}' },
        },
      ],
      plotOptions: { column: { pointPadding: 0.2, borderWidth: 0 } },
      series: [],
      data: { columns: [] },
      legend: { enabled: false },
    },
    device: {
      chart: { type: 'pie', backgroundColor: theme.palette.common.bgColor },
      credits: { enabled: false }, // remove highChart Logo
      title: {
        text: t('디바이스별 예산 비중도'),
        style: { fontWeight: 'bold' },
      },
      yAxis: { min: 0, title: { text: '' }, reversed: true },
      tooltip: { shared: true },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: { enabled: false },
          showInLegend: true,
        },
      }, // series: obj.series,
      series: [],
      exporting: { enabled: false },
      legend: { enabled: false },
    },
  };

  const [optionList, setOptionList] = useState(initOptions);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    budgetCategoryGraph({
      params: {
        ...params,
        uri: '/report/budget/category',
      },
    })
      .then((response) => {
        const { data } = response.data;
        let newOptions = { ...initOptions };
        for (let mainKey in data) {
          const newDataKey = Object.keys(data[mainKey]);
          let newData = [];
          const length = data[mainKey][newDataKey[0]]['data'].length;
          if (mainKey === 'category') {
            for (let i = 0; i < length; i++) {
              newData.push({
                y: data[mainKey]['DD_EXHS_AMT']['data'][i]['val'],
                name: data[mainKey]['CTGR_NM']['data'][i]['val'],
                DD_EXHS_RATE: data[mainKey]['DD_EXHS_RATE']['data'][i]['val'],
              });
            }
            newOptions[mainKey] = {
              ...newOptions[mainKey],
              series: [
                {
                  'name': '카테고리',
                  'data': newData,
                },
              ],
              xAxis: {
                ...newOptions[mainKey]['xAxis'],
                tickAmount: 2,
              },
              yAxis: {
                title: {
                  text: '(' + t('백만') + ')',
                  align: 'high',
                  textAlign: 'right',
                  rotation: 0,
                  offset: 0,
                  margin: 0,
                  y: -10,
                  x: -10,
                },
                labels: {
                  format: '{value:.,0f}',
                  formatter: function () {
                    return setNumberFormat(this.value / 1000000);
                  },
                },
              },
              tooltip: {
                formatter: function () {
                  let sHtml =
                    '<b>[' +
                    t('카테고리') +
                    ']<br><b>' +
                    '<b>' +
                    this.points[0].key +
                    '</b>'; // L1, L2, ... 등
                  sHtml +=
                    '<br><b>- ' +
                    t('예산 비중도') +
                    ': </b>' +
                    setNumberFormat(
                      this.points[0].point['DD_EXHS_RATE'],
                      'percent',
                      '0,0.0'
                    ) +
                    '<br><b>- ' +
                    t('예산 금액') +
                    ': </b>' +
                    setNumberFormat(this['y']) +
                    t('원');
                  return sHtml;
                },
                shared: true,
              },
            };
          } else if (mainKey === 'product') {
            let newX = [];
            const colors = ['#998fcf', '#f5e6b2', '#ffa1a1'];

            for (let i = 0; i < length; i++) {
              newData.push({
                y: data[mainKey]['DD_EXHS_AMT']['data'][i]['val'],
                name: data[mainKey]['ADVRTS_PRDT_CODE_STR']['data'][i]['val'],
                DD_EXHS_AMT: data[mainKey]['DD_EXHS_RATE']['data'][i]['val'],
                color: colors[i],
              });
            }
            for (let zz of data[mainKey]['ADVRTS_PRDT_CODE_STR']['data']) {
              newX.push(zz['val']);
            }
            newOptions[mainKey] = {
              ...newOptions[mainKey],
              yAxis: {
                title: {
                  text: '(' + t('백만') + ')',
                  align: 'high',
                  textAlign: 'right',
                  rotation: 0,
                  offset: 0,
                  margin: 0,
                  y: -10,
                  // x: -65,
                },
                labels: {
                  format: '{value:.,0f}',
                  formatter: function () {
                    return setNumberFormat(this.value / 1000000);
                  },
                },
              },
              xAxis: {
                categories: newX,
                tickPixelInterval: 150,
                tickAmount: 10,
              },
              // setNumberFormat(this.value / 1000000000);
              series: [
                {
                  'data': newData,
                },
              ],
              tooltip: {
                formatter: function () {
                  let sHtml =
                    '<b>[' +
                    t('상품') +
                    ']<br><b>' +
                    '<b>' +
                    this.points[0].key +
                    '</b>'; // L1, L2, ... 등
                  sHtml +=
                    '<br><b>- ' +
                    t('예산 비중도') +
                    ': </b>' +
                    setNumberFormat(
                      this.points[0].point['DD_EXHS_AMT'],
                      'percent',
                      '0,0.0'
                    ) +
                    '<br><b>- ' +
                    t('예산 금액') +
                    ': </b>' +
                    setNumberFormat(this['y']) +
                    t('원');
                  return sHtml;
                },
                shared: true,
              },
            };
          } else if (mainKey === 'device') {
            const colors = ['#434348', '#7cb5ec'];
            for (let i = 0; i < length; i++) {
              newData.push({
                y: data[mainKey]['DD_EXHS_AMT']['data'][i]['val'],
                name: data[mainKey]['PLTFOM_TP_CODE']['data'][i]['name'],
                DD_EXHS_RATE: data[mainKey]['DD_EXHS_RATE']['data'][i]['val'],
                color: colors[i],
              });
            }
            newOptions[mainKey] = {
              ...newOptions[mainKey],
              series: [
                {
                  'name': '등급별 총 ROAS',
                  'data': newData,
                },
              ],
              tooltip: {
                formatter: function () {
                  let sHtml =
                    '<b>[' +
                    t('디바이스') +
                    ']<br><b>' +
                    '<b>' +
                    this.point.name +
                    '</b>';
                  sHtml +=
                    '<br><b>- ' +
                    t('예산 비중도') +
                    ': </b>' +
                    setNumberFormat(
                      this.point['DD_EXHS_RATE'],
                      'percent',
                      '0,0.0'
                    ) +
                    '<br><b>- ' +
                    t('예산 금액') +
                    ': </b>' +
                    setNumberFormat(this['y']) +
                    t('원');
                  return sHtml;
                },
                shared: true,
              },
            };
          }
        }
        setOptionList(newOptions);
        setStatus('');
      })
      .catch((error) => {
        const noData =
          error.message ===
          "Cannot read properties of undefined (reading 'data')";
        if (noData === false) {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        }
        setStatus(noData ? 'noData' : 'error');
      });
    // eslint-disable-next-line
  }, [params]);

  return (
    <SwitchingArea title={t('예산 그래프')}>
      {status !== '' ? (
        <CurrentStatus type={status} />
      ) : (
        <ContentsWrap contentsCount={3}>
          <Highchart options={optionList['category']} />
          <Highchart options={optionList['product']} />
          <Highchart
            options={optionList['device']}
            helperText={{ align: 'right' }}
          />
        </ContentsWrap>
      )}
    </SwitchingArea>
  );
};

export default StatisticsGraph;
